import { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ArrowLeft,
  Play,
  Download,
  Check,
  Lightbulb,
  BookOpen,
  GraduationCap,
  Share2,
  Clock,
  X,
} from 'lucide-react';
import Artplayer from 'artplayer';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { ImageWithFallback } from './figma/ImageWithFallback';
import {
  IChatMessage,
  IVideoHistoryChatMsg,
  VideoDetail,
} from '../model/video';
import { getChatHistory, videoChat } from '../api/video';
import './videopage.css';
import quizFailureIllustration from '../assets/quiz-failure-illustration.png';
import { addSystemTimeInMsgList } from '../utils/VideoUtils';
import artplayerPluginChapter from 'artplayer-plugin-chapter';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

interface VideoPageProps {
  videoDetail: VideoDetail;
  videoId: string;
  userid?: string | null;
  question: string;
  onBack: () => void;
  onShare: () => void;
  onShowLogin: () => void;
}

function getStrSliceByLen(str: string, len: number): string {
  return `${str.slice(0, len)}${str.length > 16 ? '...' : ''}`;
}

// interface ChatMessage {
//   role: 'assistant' | 'user' | 'system';
//   content: string | null;
//   timestamp: string;
//   hasJumpButton?: boolean;
//   jumpTime?: number;
//   loading?: boolean;
// }
function getMsgListByHistoryList(list: IVideoHistoryChatMsg[]): IChatMessage[] {
  let chatlist: IChatMessage[] = [];
  list.map((item) => {
    if (item.query) {
      chatlist.push({
        role: 'user',
        content: item.query.split('\n'),
        jumpTime: 0,
        hasJumpButton: false,
        timestamp: new Date((item.ts || 0) * 1000),
      });
    }
    if (item.answer) {
      chatlist.push({
        role: 'assistant',
        content: item.answer.split('\n'),
        jumpTime: item.jump,
        hasJumpButton: !!item.jump,
        timestamp: new Date((item.ts || 0) * 1000),
      });
    }
  });
  return chatlist;
}

export function VideoPage({
  videoDetail,
  videoId,
  userid,
  question,
  onShowLogin,
  onBack,
}: VideoPageProps) {
  const videoRez = videoDetail.video_rez;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isCanPlay, setIsCanPlay] = useState(false);
  const curChapterRef = useRef<number>(0);
  const timelines = videoRez.timeline || [];
  const quizList = (videoDetail.quiz_list || []).map((item) => {
    return {
      id: item.question_id,
      type: item.question_type,
      color: 'blue',
      title: item.question_role,
      question: item.question_content,
      options: item.answer_options.map((opitem) => {
        return {
          id: opitem.option_id,
          text: opitem.option_content,
          isCorrect: opitem.option_is_correct,
          optionFeedback: opitem.option_feedback,
        };
      }),
      correctFeedback:
        '回答正确！\n太棒了！你已经掌握了基础知识的方法，继续加油，请继续攻克其他题目！',
    };
  });
  const videoEleRef = useRef<Artplayer>(null);
  const chatHistoryLastRef = useRef<string>('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'steps' | 'quiz' | 'next'>(
    'steps',
  );
  // const [isMuted, setIsMuted] = useState(false);
  // const [currentTime, setCurrentTime] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: number]: string;
  }>({});
  const [submittedQuestions, setSubmittedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [chatMessages, setChatMessages] = useState<IChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  // const [isLoading, setIsLoading] = useState(false);
  // Auto scroll when messages change
  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollAreaRef.current) {
        // Try multiple selectors for the scroll container
        const scrollContainer =
          scrollAreaRef.current.querySelector(
            '[data-radix-scroll-area-viewport]',
          ) ||
          scrollAreaRef.current.querySelector('.scroll-area-viewport') ||
          scrollAreaRef.current.querySelector('[data-scroll-area-viewport]') ||
          scrollAreaRef.current;

        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      }
    }, 50);
  };
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, isThinking]);
  const initPlayer = () => {
    console.log(videoEleRef.current);
    const video_rez = videoDetail?.video_rez;
    const timelines = video_rez?.timeline || [];
    // 视频
    if (
      !videoEleRef.current &&
      video_rez &&
      document.getElementById('my-mui-player')
    ) {
      if (video_rez.video_url) {
        videoEleRef.current = new Artplayer({
          container: '#my-mui-player',
          url: video_rez.video_url,
          setting: false,
          lang: 'en',
          fullscreen: true,
          fullscreenWeb: true,
          theme: '#ffe75c',
          icons: {
            state: `<img class="rounded-full shadow-xl" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-01/4c175c14-122c-45f5-be05-882f5932a6c5/f5a3b75f-3d10-4711-8339-206d2346ce57.png" />`,
            play: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/eab7178c-2e9e-46e0-9d91-873dcf8c6014/04963fb4-f032-4a9c-a0da-86213ba9ba2a.png" />`,
            pause: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/0348d738-bdd6-4deb-ae77-3840d9211d74/878827dc-b991-4e01-8cc1-c85d23de3f63.png" />`,
            volume: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/436eeeac-e5bd-4984-abfc-c710f4ee3e0f/91f0d803-a2d6-4c75-9954-04712e5a0d31.png" />`,
            volumeClose: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/54735e5e-861a-4c8f-ba60-4cb182938dd6/c2ef8392-0fe5-43e9-a7da-10ad40463abb.png" />`,
            fullscreenOn: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/815074a4-977d-4a2e-b6cb-66ded742dd4b/a8f6d0b4-152d-4f9a-a5bf-a944d88e89cc.png" />`,
            fullscreenOff: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/e06615fe-6cbc-43dc-99d0-f5f87577c7ca/2851281d-22a9-42ba-a60a-a3d935eafd38.png" />`,
            fullscreenWebOn: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/49f3e965-1c10-4b03-9a9c-0218f92cf294/84b85921-30f0-4e76-b051-98cf4ea264bd.png" />`,
            fullscreenWebOff: `<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/6cc0a80f-2771-431c-8e11-b492566580f8/1772a74d-e08d-495e-b71b-163e91875d5a.png" />`,
          },
          poster:
            videoDetail?.video_rez?.cover_img ||
            'https://cdn.jumpstar.app/solve/ai_gen/20251201/9f2c75b4-6e7d-4380-9ca5-c88d066b5c1f.png',
          plugins: [
            artplayerPluginChapter({
              chapters: timelines.map((tl_item, index) => ({
                start: tl_item.start_time,
                end:
                  index + 1 === timelines.length ? Infinity : tl_item.end_time,
                title: tl_item.title,
              })),
            }),
          ],
        });
        videoEleRef.current.controls.update({
          name: 'volume',
          ...videoEleRef.current.controls['volume'],
          position: 'right',
          index: 50,
        });
        videoEleRef.current.controls.add({
          name: 'epubsleftblank',
          position: 'left',
          index: 40,
          html: `<div style="width:30px;height:2px;opacity:0;"></div>`,
        });
        videoEleRef.current.on('play', () => {
          setIsPlaying(true);
        });
        videoEleRef.current.on('pause', () => {
          setIsPlaying(false);
        });
        videoEleRef.current.on('video:timeupdate', () => {
          const curtime = videoEleRef.current.currentTime;
          const playChartIndex = timelines.findIndex(
            (item) => item.start_time <= curtime && item.end_time >= curtime,
          );
          if (
            playChartIndex < timelines.length &&
            playChartIndex !== curChapterRef.current
          ) {
            curChapterRef.current = playChartIndex;
            videoEleRef.current.controls.update({
              name: 'epubs',
              position: 'left',
              index: 50,
              html: `<div style="display:flex;align-item:center">${playChartIndex + 1}. ${getStrSliceByLen(timelines[curChapterRef.current].title || '', 16)}<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/79f429cd-9a04-4f87-9ba2-b13a9920c235/bff2bf11-b326-4085-8ae0-86c8668d2e8b.png" /></div>`,
              selector: timelines.map((cht_item, cht_index) => ({
                chap_start_t: cht_item.start_time,
                chap_end_t: cht_item.end_time,
                chap_index: cht_index,
                chap_title: cht_item.title,
                default: cht_index == 0 ? true : false,
                html: `<div style="color:white;text-align:left;width:100%">${cht_index + 1}. ${cht_item.title || ''}</div>`,
              })),
              onSelect: (item, $dom) => {
                // videoEleRef.current.seek = item.chap_start_t;
                // videoEleRef.current.play();
                // console.log(item, $dom);
                handleJumpToTime(item.chap_start_t);
                return `<div style="display:flex;align-item:center">${item.chap_index + 1}. ${getStrSliceByLen(item.chap_title || '', 16)}<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/79f429cd-9a04-4f87-9ba2-b13a9920c235/bff2bf11-b326-4085-8ae0-86c8668d2e8b.png" /></div>`;
              },
            });
          }
        });
        videoEleRef.current.on('ready', () => {
          setIsCanPlay(true);
          curChapterRef.current = 0;
          videoEleRef.current.controls.add({
            name: 'epubs',
            position: 'left',
            index: 50,
            html: `<div style="display:flex;align-item:center">1. ${getStrSliceByLen(timelines[curChapterRef.current]?.title || '', 16)}<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/79f429cd-9a04-4f87-9ba2-b13a9920c235/bff2bf11-b326-4085-8ae0-86c8668d2e8b.png" /></div>`,
            selector: timelines.map((cht_item, cht_index) => ({
              chap_start_t: cht_item.start_time,
              chap_end_t: cht_item.end_time,
              chap_index: cht_index,
              chap_title: cht_item.title,
              default: cht_index == 0 ? true : false,
              html: `<div style="color:white;text-align:left;width:100%">${cht_index + 1}. ${cht_item.title || ''}</div>`,
            })),
            onSelect: (item, $dom) => {
              // videoEleRef.current.seek = item.chap_start_t;
              // videoEleRef.current.play();
              // console.log(item, $dom);
              handleJumpToTime(item.chap_start_t);
              return `<div style="display:flex;align-item:center">${item.chap_index + 1}. ${getStrSliceByLen(item.chap_title || '', 16)}<img style="width:16px;" src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-12-03/79f429cd-9a04-4f87-9ba2-b13a9920c235/bff2bf11-b326-4085-8ae0-86c8668d2e8b.png" /></div>`;
            },
          });
        });
      } else {
        toast.info('video url not find');
      }
    }
  };
  useEffect(() => initPlayer(), []);
  useEffect(() => initPlayer(), [videoDetail]);
  useEffect(() => {
    // initPlayer();
    // 聊天
    (async () => {
      try {
        if (userid) {
          const list = await getChatHistory(videoId);
          if (list.history_list && list.history_list.length) {
            chatHistoryLastRef.current = list.ref_id;
            // let chatlist: IChatMessage[] = [];
            // list.history_list.map((item) => {
            //   if (item.query) {
            //     chatlist.push({
            //       role: 'user',
            //       content: item.query.split('\n'),
            //       jumpTime: 0,
            //       hasJumpButton: false,
            //       timestamp: new Date((item.ts || 0) * 1000),
            //     });
            //   }
            //   if (item.answer) {
            //     chatlist.push({
            //       role: 'assistant',
            //       content: item.answer.split('\n'),
            //       jumpTime: item.jump,
            //       hasJumpButton: !!item.jump,
            //       timestamp: new Date((item.ts || 0) * 1000),
            //     });
            //   }
            // });
            const chatlist = getMsgListByHistoryList(list.history_list);
            setChatMessages(addSystemTimeInMsgList(chatlist));
          } else {
            chatHistoryLastRef.current = '';
            setChatMessages(
              addSystemTimeInMsgList([
                {
                  role: 'assistant',
                  content: [
                    "Hello! I'm your AI learning assistant. I can help you understand this topic. Feel free to ask me any questions while watching the video!",
                  ],
                  timestamp: new Date(),
                },
              ]),
            );
          }
        } else {
          setChatMessages(
            addSystemTimeInMsgList([
              {
                role: 'assistant',
                content: [
                  "Hello! I'm your AI learning assistant. I can help you understand this topic. Feel free to ask me any questions while watching the video!",
                ],
                timestamp: new Date(),
              },
            ]),
          );
        }
      } catch (e) {
        toast.error(e.message || 'get history error');
      }
    })();
  }, [userid]);
  const handlePlay = () => {
    if (isCanPlay) {
      videoEleRef.current.play();
    } else {
      toast.info('play wait the video is loaded');
    }
  };
  useEffect(() => {
    setTimeout(() => {
      // console.log(scrollAreaRef.current);
      if (scrollAreaRef.current) {
        if (scrollAreaRef.current) {
          // Try multiple selectors for the scroll container
          const scrollContainer =
            scrollAreaRef.current.querySelector(
              '[data-radix-scroll-area-viewport]',
            ) ||
            scrollAreaRef.current.querySelector('.scroll-area-viewport') ||
            scrollAreaRef.current.querySelector(
              '[data-scroll-area-viewport]',
            ) ||
            scrollAreaRef.current;
          scrollContainer.addEventListener('scroll', async (e) => {
            try {
              if (scrollContainer.scrollTop > 0) {
                return;
              }
              if (!userid) {
                toast.info('user not find');
                return;
              }
              if (!chatHistoryLastRef.current) {
                // toast.info('chat history not find');
                return;
              }
              if (
                scrollContainer.scrollTop <= 0 &&
                chatHistoryLastRef.current
              ) {
                const list = await getChatHistory(
                  videoId,
                  chatHistoryLastRef.current,
                );
                if (list.history_list && list.history_list.length) {
                  chatHistoryLastRef.current = list.ref_id;
                  // let chatlist: IChatMessage[] = [];
                  // list.history_list.map((item) => {
                  //   if (item.query) {
                  //     chatlist.push({
                  //       role: 'user',
                  //       content: item.query.split('\n'),
                  //       jumpTime: 0,
                  //       hasJumpButton: false,
                  //       timestamp: new Date((item.ts || 0) * 1000),
                  //     });
                  //   }
                  //   if (item.answer) {
                  //     chatlist.push({
                  //       role: 'assistant',
                  //       content: item.answer.split('\n'),
                  //       jumpTime: item.jump,
                  //       hasJumpButton: !!item.jump,
                  //       timestamp: new Date((item.ts || 0) * 1000),
                  //     });
                  //   }
                  // });
                  const chatlist = getMsgListByHistoryList(list.history_list);
                  setChatMessages((prev) =>
                    addSystemTimeInMsgList(chatlist.concat(prev)),
                  );
                } else {
                  chatHistoryLastRef.current = '';
                }
              }
            } catch (e) {
              toast.error(e.message || 'get history error');
            }
          });
        }
      }
    }, 1000);
  }, []);

  const handleSendMessage = async () => {
    if (!userid) {
      toast.info('please login');
      onShowLogin();
      return;
    }
    if (isThinking) {
      toast.info('The ai robot is thinking, please wait...');
      return;
    }
    if (!inputMessage.trim()) return;

    const newMessage: IChatMessage = {
      role: 'user',
      content: [inputMessage],
      timestamp: new Date(),
    };

    setChatMessages([...chatMessages, newMessage]);
    setInputMessage('');
    try {
      setIsThinking(true);
      const res = await videoChat(inputMessage, videoDetail.video_id);
      if (res) {
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: [res.text],
            timestamp: new Date(),
            jumpTime: res.jump,
            hasJumpButton: !!res.jump,
          },
        ]);
        setIsThinking(false);
      }
    } catch (e) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: ['Something went wrong, please retry'],
          timestamp: new Date(),
        },
      ]);
      setIsThinking(false);
      toast.error(e.message || 'request fail');
    }
  };
  const appendNode2Body = (node) => {
    try {
      document.body.appendChild(node);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const handleShare = () => {
    const input = document.createElement('textarea');
    appendNode2Body(input);
    input.readOnly = true;
    input.value = location.href;
    input.select();

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('Video link copied to clipboard!');
      // return successful
    } catch (e) {
      toast.error('copy failed');
      return false;
    }
    // navigator.clipboard.writeText(`${location.href}`);
  };
  const getFileNameFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.substring(pathname.lastIndexOf('/') + 1);
    } catch (e) {
      return null;
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(videoRez.video_url, {
        method: 'GET', // *GET, POST, PUT, DELETE, etc.
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'omit', // include, *same-origin, omit
        redirect: 'follow', // manual, *follow, error
        referrerPolicy: 'origin', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        // body: JSON.stringify(data), // body data type must match "Content-Type" header
      });
      if (!response.ok) {
        throw new Error('network error');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download =
        getFileNameFromUrl(videoRez.video_url) || `video_${videoId}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl); // 释放Blob URL
      toast.info('download success');
    } catch (error) {
      toast.error(error.message || 'download failed');
    }
  };

  const handleJumpToTime = (time: number) => {
    if (videoEleRef.current) {
      videoEleRef.current.seek = time;
      videoEleRef.current.play();
      toast.success(`Jumping to ${formatSeconds(time)}`);
    } else {
      toast.warning(`Cannot find video`);
    }
  };

  const handleAnswerSelect = (questionId: string, answerId: string) => {
    if (submittedQuestions.has(questionId)) return;
    setSelectedAnswers({ ...selectedAnswers, [questionId]: answerId });
  };

  const handleSubmitAnswer = (questionId: string) => {
    setSubmittedQuestions(new Set([...submittedQuestions, questionId]));
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const formatSeconds = (sec: number) => {
    const se = Math.floor(sec);
    const s = se % 60;
    const mi = Math.floor(se / 60);
    const m = mi % 60;
    const ho = Math.floor(mi / 60);
    return `${ho ? (ho > 9 ? ho : '0' + ho) : ''}${ho ? ':' : ''}${mi ? (m > 9 ? m : '0' + m) : '00'}:${s > 9 ? s : '0' + s}`;
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm opacity-0">
        <div className="max-w-[1280px] mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-3 text-gray-700 hover:text-gray-900 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Back to Home</span>
          </button>
          <h1 className="font-bold text-gray-800">Littlelights.ai</h1>
          <div className="w-20" /> {/* Spacer */}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1280px] mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
          {/* Left Column - Video Player */}
          <div className="space-y-6">
            {/* Video Card */}
            <Card className="overflow-hidden border border-gray-100 shadow-sm rounded-xl">
              <div className="p-6 pb-4">
                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  {videoRez.title}
                </h2>

                {/* Subject Badge and Generation Time */}
                <div className="flex items-center gap-3 mb-4">
                  <Badge className="bg-primary/20 text-black hover:bg-primary/20 rounded-full px-3 py-1 border border-black/5">
                    {videoRez.subject}
                  </Badge>
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>
                      {formatTimeAgo(new Date(videoDetail.create_ts * 1000))}
                    </span>
                  </div>
                </div>

                {/* Question Text with Thumbnail Image */}
                <div className="flex items-start gap-4 mb-4">
                  <p className="text-sm text-gray-600 flex-1 leading-relaxed">
                    {/* {videoRez.question} */}
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]} // 解析数学公式
                      rehypePlugins={[rehypeKatex]} // 渲染数学公式
                    >
                      {videoRez.question}
                    </ReactMarkdown>
                  </p>
                  {videoDetail.image ? (
                    <ImageWithFallback
                      src={videoDetail.image}
                      alt="Math problem"
                      className="w-20 h-20 rounded-lg object-cover shrink-0 shadow-sm"
                    />
                  ) : null}
                </div>

                {/* Video Player */}
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-3">
                  <div
                    id="my-mui-player"
                    className="w-full h-full relative z-2"
                  ></div>
                  {/* 没有逻辑作用，只给播放器播放按钮提供相应class样式 */}
                  {isPlaying ? null : (
                    <div className="absolute -z-10 opacity-0 left-0 top-0">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-xl cursor-pointer hover:scale-110 transition-transform">
                        <Play
                          className="w-7 h-7 text-black ml-1"
                          fill="currentColor"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Video Controls */}
                <div className="flex items-center justify-end">
                  {/* <Button
                    onClick={handlePlay}
                    className="bg-primary hover:bg-primary/90 text-black rounded-full gap-2 h-10 px-6 border-2 border-black/10"
                  >
                    <Play className="w-4 h-4" fill="currentColor" />
                    <span className="text-sm font-semibold">Watch Video</span>
                  </Button> */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      className="text-gray-600 hover:text-gray-900 gap-2 h-9"
                      onClick={handleShare}
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="text-sm">Share</span>
                    </Button>
                    <Button
                      onClick={handleDownload}
                      variant="ghost"
                      className="text-gray-600 hover:text-gray-900 gap-2 h-9"
                    >
                      <Download className="w-4 h-4" />
                      <span className="text-sm">Download</span>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {/* Tabs - Sliding Card Style */}
            <div className="relative bg-white rounded-2xl shadow-md border-2 border-black/5 p-1 flex gap-1.5">
              <Button
                variant={activeTab === 'steps' ? 'default' : 'ghost'}
                className={`flex-1 rounded-xl h-9 transition-all duration-300 ${
                  activeTab === 'steps'
                    ? 'bg-primary text-black shadow-lg scale-105'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab('steps')}
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Key Steps
              </Button>
              <Button
                variant="ghost"
                className={`flex-1 rounded-xl h-9 transition-all duration-300 ${
                  activeTab === 'quiz'
                    ? 'bg-primary text-black shadow-lg scale-105'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab('quiz')}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Quiz
              </Button>
              {/* <Button
                variant="ghost"
                className={`flex-1 rounded-xl h-9 transition-all duration-300 ${
                  activeTab === "next"
                    ? "bg-primary text-black shadow-lg scale-105"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
                onClick={() => setActiveTab("next")}
              >
                <Play className="w-4 h-4 mr-2" />
                Next Video
              </Button> */}
            </div>

            {/* Tab Content - Key Steps */}
            {activeTab === 'steps' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {timelines.map((step, stIdx) => (
                  <Card
                    key={step.start_time}
                    className="overflow-hidden border-2 border-black/5 shadow-lg rounded-2xl bg-linear-to-br from-primary/5 to-primary/10 hover:shadow-xl hover:border-primary/30 transition-all hover:scale-105"
                  >
                    <div className="p-4 flex flex-col justify-between h-full">
                      <div className="flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-black rounded-full flex items-center justify-center shrink-0">
                              <span className="text-primary font-bold text-xs">
                                {stIdx + 1}
                              </span>
                            </div>
                            <h4 className="font-semibold text-gray-900 text-sm">
                              Step{stIdx + 1}
                            </h4>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">
                            {formatSeconds(step.start_time)}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="mb-3">
                          <h5 className="font-semibold text-gray-900 mb-1 text-sm line-clamp-2">
                            {step.title}
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">
                            {step.subtitle}
                          </p>
                        </div>
                      </div>

                      {/* Jump Button */}
                      <Button
                        onClick={() => handleJumpToTime(step.start_time)}
                        variant="outline"
                        className="w-full rounded-lg h-8 gap-1.5 text-xs"
                      >
                        <Play className="w-3 h-3" />
                        Jump
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Tab Content - Quiz */}
            {activeTab === 'quiz' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                {(quizList || []).length == 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-6">
                    {/* Quiz Failure Illustration */}
                    <div className="w-[134px] h-[134px] mb-6 relative flex items-center justify-center">
                      <img
                        src={quizFailureIllustration}
                        alt="Quiz generation failed"
                        className="max-w-full max-h-full object-contain"
                        style={{ width: '134px', height: '134px' }}
                      />
                    </div>

                    {/* Error Message */}
                    <p className="text-[#4a5565] text-[13px] text-center leading-[20px] tracking-[-0.1504px]">
                      No quiz provided for this question.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-6">
                      <GraduationCap className="w-6 h-6 text-gray-800" />
                      <h3 className="text-xl font-bold text-gray-800">Quiz</h3>
                      <span className="text-sm text-gray-500 ml-auto">
                        Strengthen skills and improve abilities
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {quizList.map((quiz, qidx) => {
                        const isSubmitted = submittedQuestions.has(quiz.id);
                        const selectedAnswer = selectedAnswers[quiz.id];
                        const selectedOption = quiz.options.find(
                          (opt) => opt.id === selectedAnswer,
                        );
                        const showFeedback = isSubmitted && selectedOption;

                        return (
                          <Card
                            key={quiz.id}
                            className="overflow-hidden border-2 border-black/5 shadow-lg rounded-2xl bg-linear-to-br from-primary/5 to-primary/10 hover:shadow-xl hover:border-primary/30 transition-all"
                          >
                            <div className="p-5">
                              {/* Header */}
                              <div className="flex items-start gap-3 mb-4">
                                <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center shrink-0">
                                  <span className="text-primary font-bold">
                                    {qidx + 1}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-black mb-1">
                                    {quiz.type}
                                  </p>
                                  <h4 className="font-semibold text-gray-900 text-sm">
                                    {quiz.title}
                                  </h4>
                                </div>
                              </div>

                              {/* Question */}
                              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                                <ReactMarkdown
                                  remarkPlugins={[remarkMath]} // 解析数学公式
                                  rehypePlugins={[rehypeKatex]} // 渲染数学公式
                                >
                                  {quiz.question}
                                </ReactMarkdown>
                              </p>

                              {/* Options */}
                              <div className="space-y-2 mb-4">
                                {quiz.options.map((option) => {
                                  const isSelected =
                                    selectedAnswer === option.id;
                                  const showCorrect =
                                    isSubmitted && option.isCorrect;
                                  const showIncorrect =
                                    isSubmitted &&
                                    isSelected &&
                                    !option.isCorrect;

                                  let optionBgClass = 'bg-white';
                                  if (showCorrect) {
                                    optionBgClass =
                                      'bg-primary/30 border-primary';
                                  } else if (showIncorrect) {
                                    optionBgClass = 'bg-red-100 border-red-300';
                                  } else if (isSelected) {
                                    optionBgClass =
                                      'bg-primary/10 border-primary/30';
                                  }

                                  return (
                                    <button
                                      key={option.id}
                                      onClick={() =>
                                        handleAnswerSelect(quiz.id, option.id)
                                      }
                                      disabled={isSubmitted}
                                      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors text-sm ${optionBgClass} ${
                                        isSubmitted
                                          ? 'cursor-default'
                                          : 'hover:bg-gray-50 cursor-pointer'
                                      } flex items-center justify-between gap-2`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <div
                                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                            isSelected
                                              ? 'border-gray-600'
                                              : 'border-gray-300'
                                          }`}
                                        >
                                          {isSelected && (
                                            <div className="w-2 h-2 bg-gray-600 rounded-full" />
                                          )}
                                        </div>
                                        <span className="text-gray-700">
                                          <ReactMarkdown
                                            remarkPlugins={[remarkMath]} // 解析数学公式
                                            rehypePlugins={[rehypeKatex]} // 渲染数学公式
                                          >
                                            {option.text}
                                          </ReactMarkdown>
                                        </span>
                                      </div>
                                      {showCorrect && (
                                        <Check className="w-4 h-4 text-green-600 shrink-0" />
                                      )}
                                      {showIncorrect && (
                                        <X className="w-4 h-4 text-red-600 shrink-0" />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Feedback */}
                              {showFeedback && (
                                <div
                                  className={`p-3 rounded-lg mb-3 text-xs leading-relaxed ${
                                    selectedOption.isCorrect
                                      ? 'bg-primary/20 text-black border border-primary/30'
                                      : 'bg-red-100 text-red-800 border border-red-300'
                                  }`}
                                >
                                  {selectedOption.optionFeedback}
                                </div>
                              )}

                              {/* Submit Button */}
                              <Button
                                onClick={() => handleSubmitAnswer(quiz.id)}
                                disabled={!selectedAnswer || isSubmitted}
                                className={`w-full h-9 rounded-lg font-semibold ${
                                  isSubmitted
                                    ? 'bg-gray-400 text-white cursor-default'
                                    : 'bg-black hover:bg-black/90 text-primary'
                                }`}
                              >
                                {isSubmitted ? 'Submitted' : 'Submit'}
                              </Button>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Tab Content - Next Video */}
            {activeTab === 'next' && (
              <Card className="overflow-hidden border-2 border-black/5 shadow-lg rounded-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="p-6">
                  {/* Section Title */}
                  <div className="flex items-center gap-2 mb-4">
                    <GraduationCap className="w-6 h-6 text-gray-800" />
                    <h3 className="text-xl font-bold text-gray-800">
                      Next Video
                    </h3>
                  </div>

                  {/* Video Preview Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left - Video Preview */}
                    <div className="space-y-4">
                      <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                        <div className="absolute inset-0 bg-linear-to-br from-primary/30 via-primary/20 to-primary/10" />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-xl cursor-pointer hover:scale-110 transition-transform">
                            <Play
                              className="w-7 h-7 text-black ml-1"
                              fill="currentColor"
                            />
                          </div>
                        </div>
                        <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-2 py-1 rounded">
                          4:25
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Badge className="bg-primary/20 text-black hover:bg-primary/20 rounded-full px-3 py-1 border border-black/5">
                          Mathematics
                        </Badge>
                        <div>
                          <h4 className="font-bold text-lg text-gray-800 mb-1">
                            Fraction Distribution
                          </h4>
                          <p className="text-sm text-gray-600">
                            Learn fraction division for everyday problems.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right - Learning Details */}
                    <div className="space-y-6">
                      {/* Learning Goals */}
                      <div>
                        <h5 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-gray-300" />
                          Learning Goals
                        </h5>
                        <div className="space-y-2">
                          <div className="bg-primary/10 rounded-lg p-3 flex items-start gap-3 border border-primary/20">
                            <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center shrink-0 mt-0.5">
                              <span className="text-primary text-xs font-bold">
                                1
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              Apply fractions in daily scenarios
                            </p>
                          </div>
                          <div className="bg-primary/10 rounded-lg p-3 flex items-start gap-3 border border-primary/20">
                            <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center shrink-0 mt-0.5">
                              <span className="text-primary text-xs font-bold">
                                2
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              Solve distribution problems correctly
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Key Points */}
                      <div>
                        <h5 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                          <Lightbulb className="w-5 h-5" />
                          Key Points
                        </h5>
                        <div className="space-y-2">
                          <div className="bg-primary/10 rounded-lg p-3 flex items-center gap-3 border border-primary/20">
                            <Check className="w-4 h-4 shrink-0" />
                            <p className="text-sm text-gray-700">
                              Understand practical fraction meanings
                            </p>
                          </div>
                          <div className="bg-primary/10 rounded-lg p-3 flex items-center gap-3 border border-primary/20">
                            <Check className="w-4 h-4 shrink-0" />
                            <p className="text-sm text-gray-700">
                              Master fraction division methods
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Prerequisites */}
                      <div>
                        <h5 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                          <BookOpen className="w-5 h-5" />
                          Prerequisites
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          <Badge className="bg-primary/20 text-black hover:bg-primary/20 rounded-full px-3 py-1.5 border border-black/5">
                            Basic fractions
                          </Badge>
                          <Badge className="bg-primary/20 text-black hover:bg-primary/20 rounded-full px-3 py-1.5 border border-black/5">
                            Addition/subtraction
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - AI Assistant */}
          <Card className="overflow-hidden border-2 border-black/5 shadow-lg rounded-2xl flex flex-col lg:sticky lg:top-6 h-[800px] lg:h-[calc(100vh-120px)]">
            {/* AI Assistant Header */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-2 shrink-0 bg-primary/5">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <h4 className="font-semibold text-gray-900">AI Assistant</h4>
            </div>

            {/* Chat Messages - Scrollable */}
            <ScrollArea
              ref={scrollAreaRef}
              className="flex-1 p-4 max-h-[calc(100vh-300px)] overflow-y-auto"
            >
              <div className="space-y-4">
                {chatMessages.map((message, index) => (
                  <div
                    key={`${message.timestamp}_${message.content}_${index}`}
                    className="space-y-1"
                  >
                    {/* Message bubble */}
                    <div
                      className={`flex ${message.role === 'system' ? 'justify-center' : ''} ${message.role === 'user' ? 'justify-end' : ''}  ${message.role === 'assistant' ? 'justify-start' : ''}`}
                    >
                      {message.role === 'system' ? (
                        <p
                          className="text-[11px] leading-5 text-center tracking-[-0.1504px]"
                          style={{
                            color: '#6A7282',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                          }}
                        >
                          {message.timestamp.toLocaleDateString(
                            'en-US',
                            message.timestamp.getFullYear() ==
                              new Date().getFullYear()
                              ? {
                                  // weekday: 'short',
                                  // year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                }
                              : {
                                  // weekday: 'short',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                },
                          )}
                        </p>
                      ) : (
                        <div
                          className={`max-w-[288px] w-fit rounded-2xl p-3 ${
                            message.role === 'assistant'
                              ? 'bg-gray-100'
                              : 'bg-[#ffe75c] text-black ml-auto'
                          }`}
                        >
                          {message.content.map((msgc, jdx) => (
                            <p
                              key={`${msgc}_${jdx}`}
                              className="text-sm leading-relaxed wrap-break-words"
                            >
                              {msgc}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Jump button (only for assistant messages with jump functionality) */}
                    {message.role === 'assistant' &&
                      message.hasJumpButton &&
                      message.jumpTime && (
                        <div className="flex justify-start">
                          <button
                            onClick={() => handleJumpToTime(message.jumpTime!)}
                            className="text-sm text-[#3e7ddb] underline hover:no-underline ml-4"
                          >
                            Jump to {formatSeconds(message.jumpTime)}👆
                          </button>
                        </div>
                      )}
                  </div>
                ))}

                {/* Loading indicator */}
                {isThinking && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl p-3 w-fit">
                      <div className="flex items-center gap-2">
                        <div className="bar-wave">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="text-xs text-gray-500">
                          AI is thinking...
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Chat Input - Fixed at Bottom */}
            <div className="p-4 border-t border-gray-100 shrink-0 bg-white">
              <div className="space-y-3">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Ask me any questions about this lesson..."
                  className="w-full bg-white border-2 border-black/10 rounded-2xl px-4 py-3 resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none min-h-[44px] max-h-32 overflow-y-auto"
                  rows={1}
                  style={{
                    height: 'auto',
                    minHeight: '44px',
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height =
                      Math.min(target.scrollHeight, 128) + 'px';
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isThinking}
                  className={`w-full rounded-full h-10 font-semibold transition-all ${
                    inputMessage.trim() && !isThinking
                      ? 'bg-[#302f2b] hover:bg-[#302f2b]/90 text-[#ffe75c]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                >
                  Send Message
                </button>
              </div>
            </div>
          </Card>
          {/* <Card className="overflow-hidden border-2 border-black/5 shadow-lg rounded-2xl flex flex-col lg:sticky lg:top-6 lg:h-[calc(100vh-120px)]">
            
            <div className="p-4 border-b border-gray-100 flex items-center gap-2 flex-shrink-0 bg-primary/5">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <h4 className="font-semibold text-gray-900">AI Assistant</h4>
            </div>

            
            <ScrollArea
              className="flex-none p-4"
              style={{ height: 'calc(100% - 234px)' }}
            >
              <div className="space-y-4">
                {chatMessages.map((message, index) => (
                  <div key={index} className="space-y-1">
                    
                    <div
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[288px] w-fit rounded-2xl p-3 ${
                          message.role === 'assistant'
                            ? 'bg-gray-100'
                            : 'bg-[#ffe75c] text-black ml-auto'
                        }`}
                      >
                        <p className="text-sm leading-relaxed break-words">
                          {message.content}
                        </p>
                      </div>
                    </div>

                    
                    {message.role === 'assistant' &&
                      message.hasJumpButton &&
                      message.jumpTime && (
                        <div className="flex justify-start">
                          <button
                            onClick={() => handleJumpToTime(message.jumpTime!)}
                            className="text-sm text-[#3e7ddb] text-underline ml-4"
                          >
                            Jump to {formatSeconds(message.jumpTime)}👆
                          </button>
                        </div>
                      )}
                  </div>
                ))}

                
                {isThinking ? (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl p-3 w-fit">
                      <div className="flex items-center gap-2">
                        <div className="bar-wave">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="text-xs text-gray-500">
                          AI is thinking...
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </ScrollArea>

            
            <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-white">
              <div className="space-y-3">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Ask me any questions about this lesson..."
                  className="w-full bg-white border-2 border-black/10 rounded-2xl px-4 py-3 resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none min-h-[44px] max-h-32 overflow-y-auto"
                  rows={1}
                  style={{
                    height: 'auto',
                    minHeight: '44px',
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height =
                      Math.min(target.scrollHeight, 128) + 'px';
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isThinking}
                  className={`w-full rounded-full h-10 font-semibold transition-all ${
                    inputMessage.trim() && !isThinking
                      ? 'bg-[#302f2b] hover:bg-[#302f2b]/90 text-[#ffe75c]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                >
                  Send Message
                </button>
              </div>
            </div>
          </Card> */}
        </div>
      </div>
    </div>
  );
}
